import { defineComponent as _defineComponent } from 'vue'
import { computed, nextTick, ref, Ref, watch, withDefaults, useAttrs, useListeners } from 'vue'
import merge from 'lodash/merge'
import dataSearch from './dataSearch'
import type { JtTableProps } from './types'
import { requestInterceptor, responseInterceptor } from './interceptor'
import { ResponseSchema } from '@/types/axios/public-json-structure'
import { Table } from 'element-ui'

/**
 * JtTable组件定义
 *
 */

export default /*#__PURE__*/_defineComponent({
  __name: 'index',
  props: {
    data: null,
    config: null,
    headerRowStyle: { default: () => {
      '#fafafa'
    } },
    pageSizes: { default: () => [10, 20, 30, 50] },
    pageLayout: { default: 'total, prev, pager, next, sizes, jumper' },
    pageSizeChange: null,
    pageCurrentChange: null,
    hideOnSinglePage: { type: Boolean, default: true },
    tableType: null,
    tableTypeColumnConfig: null,
    webPagination: { default: null },
    param: null,
    noPage: { type: Boolean, default: false },
    autoSearch: { type: Boolean, default: true },
    revertList: { type: Boolean, default: false },
    checkChange: { type: Function, default: () => ({}) },
    hidePaginationOnSinglePage: { type: Boolean, default: false },
    maxHeight: null,
    requestInterceptor: { default: () => requestInterceptor },
    responseInterceptor: { default: () => responseInterceptor }
  },
  emits: [
  'data-done',
  'sort-change',
],
  setup(__props: any, { expose, emit }) {

const props = __props as { data?: any[], config: JtTableProps.ColumnConfig[], headerRowStyle: any, pageSizes: number[], pageLayout: string, pageSizeChange?: ((currentPage: number, size: number) => void) | null, pageCurrentChange?: ((cur: number, pageSize: number) => void) | null, hideOnSinglePage: boolean, tableType?: 'selection' | 'index' | 'expand', tableTypeColumnConfig?: JtTableProps.TableColumnConfig<'type'>, webPagination: null | JtTableProps.WebPagination<ResponseSchema>, param: any, noPage: boolean, autoSearch: boolean, revertList: boolean, checkChange: () => void, hidePaginationOnSinglePage: boolean, maxHeight?: number | string, requestInterceptor: JtTableProps.RequestInterceptor, responseInterceptor: JtTableProps.ResponseInterceptor<ResponseSchema> }

const tableRef = ref<InstanceType<typeof Table>>()



const renderData: Ref<any[]> = ref([])

const loading = ref(false)

const page = ref({
  currentPage: 1,
  pageSize: props.noPage
    ? 9999
    : Array.isArray(props.pageSizes)
      ? props.pageSizes[0]
      : 10,
  pageTotal: 0
})

/**
 * 对传入的配置进行预处理, 补全默认项, 避免在 <template> 内写大量的三元表达式
 */
const normalizedColumnConfig = computed<JtTableProps.ColumnConfig[]>(() => {
  return props.config.map((c, idx) => {
    const defaultJtTableColConfig: Partial<JtTableProps.ColumnConfig> = {
      isSlot: c.isSlot ?? false,
      isSlotColumn: c.isSlotColumn ?? false,
      key: c.key ?? idx,
      elColProps: {
        align: 'center',
        showOverflowTooltip: true,
        headerAlign: 'center'
      }
    }

    return merge(defaultJtTableColConfig, c)
  })
})
watch(
  () => props.data,
  () => {
    props.autoSearch && getData()
  },
  {
    immediate: true
  }
)

async function pageSizeChangeSelf(size: number) {
  page.value.pageSize = size
  if (props.pageSizeChange) {
    props.pageSizeChange(page.value.currentPage, size)
    return
  }
  getData()
}


async function getData() {
  // 如果是后端分页
  if (props.webPagination) {
    loading.value = true
    try {
      await nextTick()
      const rawPaginationParams = {
        pageNum: page.value.currentPage,
        pageSize: page.value.pageSize
      }
      const customPaginationParams =
        props.requestInterceptor(rawPaginationParams)
      const rawRes = await props.webPagination({
        ...customPaginationParams,
        ...props.param
      })

      const res = props.responseInterceptor(rawRes)
      page.value.pageTotal = props.noPage // 不分页时后端返回的是 page: null
        ? 0
        : res?.page.total || 0

      renderData.value = res.data
      emit('data-done')
    } catch (e) {
      console.log(e)
    } finally {
      loading.value = false
    }
  } else {
    // 纯前端分页
    changeData(page.value.currentPage, page.value.pageSize)
  }
}

async function pageCurrentChangeSelf(cur: number) {
  page.value.currentPage = cur
  if (props.pageCurrentChange) {
    props.pageCurrentChange(cur, page.value.pageSize)
    return
  }
  getData()
}

function changeData(cur: number, size: number) {
  page.value.pageTotal = props.data?.length ?? 0
  renderData.value = dataSearch(
    props.revertList ? (props.data || []).concat().reverse() : props.data || [],
    { cur, size }
  )
}

async function search() {
  page.value.currentPage = 1
  // 如果是后端分页
  if (props.webPagination) {
    await nextTick()
    await getData()
  }
}

// 刷新当前页面
async function refreshCurrent() {
  const cur = page.value.currentPage
  pageCurrentChangeSelf(cur)
}

function clearTable() {
  renderData.value = []
  page.value = {
    ...page.value,
    currentPage: 1,
    pageTotal: 0
  }
}
const attrs = useAttrs()
const listeners = useListeners()

delete listeners['sort-change'] // 避免父组件的 sort-change 也被 el-table 继承
const sortParams: JtTableProps.TableOnSortChangeParams = {
  prop: attrs.defaultSort?.prop ?? '',
  order: attrs.defaultSort?.prop ?? null,
}

function handleSortChange (params: JtTableProps.TableOnSortChangeParams) {
  const {
    prop,
    order,
  } = params

  /** el-table 的自定义排序有 3 种状态: 'ascending' | 'descending' | null
   * 而一般的需求中只需升序/降序 2 种
   * 默认的 el-table 交互连续点击同一个排序三角(比如连着点击 2 次下三角)时,
   * 第 1 次点击后: handleSortChange({ order: 'descending'}) + 下三角高亮
   * 第 2 次点击后: handleSortChange({ order: null}) + 下三角置灰, 上三角也置灰   -> 这个和一般的需求交互都不符, 需在此时反向排序
   * 下面的代码即解决以上问题
   */
  if (order === null) { // 从某个列的排序切换为不排序状态时走该逻辑分支, 此时使用上次 prop 进行排序, 并改为和之前相反的排序
    const fixedOrder = sortParams.order === 'ascending' ? 'descending' : 'ascending'
    tableRef.value?.sort(prop, fixedOrder) // 再次触发 handleSortChange. 并且高亮 el-table 排序列三角icon
  } else {
    const elProps = props.config.find(it => it.prop === prop)?.elColProps ?? {}
    if (elProps.sortable !== undefined) {
      emit('sort-change', params)
    }
  }
}

function updateTableData(newData: any[]) {
  renderData.value = newData
}
// 代理ElTable下的方法
const innerElTableInstance = computed(() => {
  return tableRef
})
const clearSelection = computed(() => {
  return tableRef.value?.clearSelection
})
const toggleRowSelection = computed(() => {
  return tableRef.value?.toggleRowSelection
})
const toggleAllSelection = computed(() => {
  return tableRef.value?.toggleAllSelection
})
const toggleRowExpansion = computed(() => {
  return tableRef.value?.toggleRowExpansion
})
const setCurrentRow = computed(() => {
  return tableRef.value?.setCurrentRow
})
const clearSort = computed(() => {
  return tableRef.value?.clearSort
})
const clearFilter = computed(() => {
  return tableRef.value?.clearFilter
})
const doLayout = computed(() => {
  return tableRef.value?.doLayout
})
const sort = computed(() => {
  return tableRef.value?.sort
})
expose({
  updateTableData,
  loading,
  search,
  getData,
  refreshCurrent,
  clearTable,
  elTableRef: tableRef,
  innerElTableInstance: innerElTableInstance.value,
  clearSelection: clearSelection.value,
  toggleRowSelection: toggleRowSelection.value,
  toggleAllSelection: toggleAllSelection.value,
  toggleRowExpansion: toggleRowExpansion.value,
  setCurrentRow: setCurrentRow.value,
  clearSort: clearSort.value,
  clearFilter: clearFilter.value,
  doLayout: doLayout.value,
  sort: sort.value
})

return { __sfc: true,tableRef, props, renderData, loading, page, normalizedColumnConfig, pageSizeChangeSelf, emit, getData, pageCurrentChangeSelf, changeData, search, refreshCurrent, clearTable, attrs, listeners, sortParams, handleSortChange, updateTableData, innerElTableInstance, clearSelection, toggleRowSelection, toggleAllSelection, toggleRowExpansion, setCurrentRow, clearSort, clearFilter, doLayout, sort }
}

})